import type { ListItemProps } from "@studio3t/generic-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { FooterPicker } from "./FooterPicker";
import { StatusDivider } from "./JsonDocumentEditor";
import { usePipelineStaging } from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";
import {
  variantLabel,
  variantPickerHidden,
  variantSamplesBadge,
  variantTriggerLabel,
} from "./schemaVariants";
import type { PendingSeed } from "./variantSwitch";

// The schema editor's footer variant picker (TTTL-179 gap 4, the old
// Studio's SchemaVariantBar reduced to its dropdown): rendered among the
// status-bar extras while the editor shows the seeded source draft whose
// introspection clustered into several document shapes. Picking a shape
// swaps the seeded schema draft and the sample input document; edits route
// through the confirm modal (pendingVariantSwitch) before being replaced.
export function SchemaVariantDropdown() {
  const {
    sourceVariants,
    effectiveSelectedId,
    requestVariantSwitch,
    pendingVariantSwitch,
    confirmVariantSwitch,
    cancelVariantSwitch,
    variantIsCustom,
  } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  if (
    variantPickerHidden(editsLocked, sourceVariants, effectiveSelectedId) ||
    !sourceVariants
  ) {
    return null;
  }
  const { variants, activeIndex } = sourceVariants;
  const safeIndex = Math.min(activeIndex, variants.length - 1);
  const items: ListItemProps[][] = [
    variants.map((v, i) => ({
      type: "text" as const,
      id: `variant-${i}`,
      value: `variant-${i}`,
      label: variantLabel(i, v),
      badgeText: variantSamplesBadge(v),
      // A custom draft matches no variant — no dot anywhere.
      selected: {
        value: !variantIsCustom && i === safeIndex,
        icon: "dot" as const,
      },
      onClick: () => requestVariantSwitch(i),
    })),
  ];
  const label = variantTriggerLabel(
    safeIndex,
    variants[safeIndex],
    variantIsCustom,
  );
  // Figma 16421-178261 (trigger) and 16421-180155 (menu): the footer
  // picker with the variants' rows, each carrying its sample count badge.
  return (
    <>
      {/* The divider rides with the picker — none dangles after the
          converter while the picker is hidden. */}
      <StatusDivider />
      <FooterPicker
        label={label}
        name={`Schema variant: ${label}`}
        items={items}
        menuWidth={VARIANT_MENU_WIDTH}
        testId="schema-variant"
      />
      <ConfirmDeleteModal
        isOpen={pendingVariantSwitch !== null}
        title="Switch schema variant?"
        confirmLabel="Replace"
        confirmType="primary"
        message={switchMessage(pendingVariantSwitch?.edits ?? [])}
        onConfirm={confirmVariantSwitch}
        onCancel={cancelVariantSwitch}
      />
    </>
  );
}

// Figma DropdownMenu 16421-180155: the variant rows with their badges.
const VARIANT_MENU_WIDTH = 260;

// The drop-time replace-schema confirm (always mounted via StudioPanels —
// the drop happens with no schema editor on screen): a source landed on a
// slot holding a custom draft, and the introspected schema waits on the
// user's call. Cancel keeps the draft; Replace seeds over it.
export function SchemaSeedReplaceModal() {
  const { pendingSeed, confirmSeedReplace, cancelSeedReplace } =
    usePipelineStaging();
  return (
    <ConfirmDeleteModal
      isOpen={pendingSeed !== null}
      title="Replace schema draft?"
      confirmLabel="Replace"
      confirmType="primary"
      message={pendingSeed ? seedReplaceMessage(pendingSeed) : ""}
      onConfirm={confirmSeedReplace}
      onCancel={cancelSeedReplace}
    />
  );
}

// What would replace the draft: the schema already saved under the
// collection's name, or the one introspection just generated.
function seedReplaceMessage(pending: PendingSeed): string {
  const name = pending.resource.name;
  const what =
    pending.seed.kind === "stored"
      ? `A schema named "${name}" is already saved.`
      : `Introspecting "${name}" generated a schema.`;
  return `${what} Replace your unsaved schema draft with it? Your draft's content will be lost.`;
}

// "your edited input document", "…input document and schema draft" — the
// confirm names exactly what the switch would overwrite.
function switchMessage(edits: string[]): string {
  const what = edits.join(" and ") || "input document";
  return `Switching the variant replaces your edited ${what} with the selected shape's generated content.`;
}
